import React from 'react';
import css from './PlanPanel.module.scss';
import { CANNOT_DO_THIS_HERE, OPEN_THIS_SCENE } from '../viewModel/refusals';
import type { BasilPlanRow, BasilRequestStatus } from '../types/basilTypes';

/** P5: the control behind the refusal's own words. It is the protected exit. */
export { OPEN_THIS_SCENE };

/** A warning the engine returned, rendered verbatim under the request it belongs to. */
export type PlanWarning = {
  nodeId?: number | string;
  stepId?: number | string;
  answerId?: number | string;
  field: string;
  message: string;
};

/**
 * P6: Changes waits its turn.
 *
 * It says what will be here rather than reporting that nothing is, so a writer who has not typed
 * yet is not told about an empty list they did not ask for.
 */
export const CHANGES_EMPTY = 'Your edits will show up here.';

/**
 * P4: where the counts went.
 *
 * "Meant to change 1 scene, 1 line and 0 answers" is what the pipeline checks the reply against, and
 * it belongs to whoever is checking the pipeline. The row itself leads with the words the writer
 * changed, and the counts wait behind one word.
 */
export const DETAILS = 'Details';

/** What separates the before from the after. The same arrow the script spells a route with. */
const ARROW = '→';

/**
 * T17-08: a run that stopped before this request's body was built.
 *
 * It is proven unsent — the pipeline never reached the transport for this one — and it is usually
 * temporary: the double-check stopped answering, the episode's own state could not be re-proved,
 * the writer moved to another episode. "Can't do this here" is the wrong sentence for all of those,
 * because it says the change is impossible here when what happened is that this attempt stopped.
 */
export const STOPPED_BEFORE_SENDING = 'Basil stopped before sending this change';

const STATUS_CLASS: Readonly<Record<string, string | undefined>> = Object.freeze({
  refused: css.statusRefused,
  stale: css.statusStale,
  sending: css.statusSending,
  reconciled: css.statusReconciled,
  mismatched: css.statusMismatched,
  verified: css.statusVerified,
  rejected: css.statusRejected,
});

/**
 * The status vocabulary, in the words the panel shows (spec §4.11).
 *
 * Basil v0.2, P5: the three held states all read "Can't do this here", because that is what they
 * are — a limitation of this surface, with the row's own sentence saying which one and where the
 * writer goes instead. "Needs approval" suggested somebody could approve it on this screen, and
 * nobody can. The stale check NEVER wears the held label: the episode moving under the writer is
 * not a limitation of Basil, and it stops being true on its own.
 */
export const STATUS_WORDS: Readonly<Record<BasilRequestStatus, string>> = Object.freeze({
  planned: 'ready',
  refused: CANNOT_DO_THIS_HERE,
  'needs-canary': CANNOT_DO_THIS_HERE,
  'needs-owner': CANNOT_DO_THIS_HERE,
  stale: 'needs a fresh check',
  stopped: STOPPED_BEFORE_SENDING,
  queued: 'queued',
  sending: 'sending',
  reconciled: 'sent, not confirmed yet',
  mismatched: 'not confirmed',
  verified: 'confirmed',
  rejected: "couldn't be confirmed",
});

/**
 * T17-08: the four detailed status words, and where they go.
 *
 * They describe where a request stands in a pipeline — sent but unconfirmed, not confirmed,
 * confirmed, could not be confirmed — and the brief puts them in Details, with the counts, for
 * whoever is checking the pipeline. The recovery view says the same facts in whole sentences with
 * real numbers, which is what a writer reads.
 */
export const DETAILED_STATUSES: readonly BasilRequestStatus[] = Object.freeze([
  'reconciled', 'mismatched', 'verified', 'rejected',
]);

/** How a detailed status reads inside Details. */
export const statusDetail = (status: BasilRequestStatus): string => `Status: ${STATUS_WORDS[status]}`;

/** What the plan panel needs. */
export type PlanPanelProps = {
  /** One row per request, plus one per structural item. */
  rows: BasilPlanRow[];
  /** The engine's own warnings, keyed by the row they belong to. */
  warningsByRow?: Record<string, PlanWarning[]>;
  /** A 422's `details[]`, joined and shown verbatim. */
  details?: Array<{ message: string }>;
  /**
   * P5: ask to open the scene a held row names. The panel never leaves on its own; the surface
   * decides, and it asks first whenever edits are unapplied.
   */
  onOpenScene?: (branchId: number) => void;
};

/**
 * One row per REQUEST, with the chunk shown as a grouping band. `warnings[]` render one row per
 * warning under the offending request, showing `field` and `message` verbatim; a 422's details are
 * shown verbatim too.
 */
export function PlanPanel(props: PlanPanelProps) {
  const {
    rows, warningsByRow, details, onOpenScene,
  } = props;

  if (rows.length === 0) {
    return (
      <section className={css.list} aria-label="Changes">
        <p className={css.empty}>{CHANGES_EMPTY}</p>
      </section>
    );
  }
  const chunks = Array.from(new Set(rows.map((row) => row.chunkIndex))).sort((a, b) => a - b);

  return (
    <section className={css.list} aria-label="Changes">
      {chunks.map((chunkIndex) => (
        <div key={`chunk-${chunkIndex}`}>
          {chunks.length > 1 ? (
            <p className={css.chunkBand}>{`Group ${chunkIndex + 1} — these are sent together`}</p>
          ) : null}
          {rows.filter((row) => row.chunkIndex === chunkIndex).map((row) => {
            const detailed = DETAILED_STATUSES.indexOf(row.status) !== -1;

            return (
              <div className={css.request} key={row.key}>
                <span className={row.words ? css.title : undefined}>{row.words ? row.words.title : row.phrase}</span>
                {/* T17-08: the four pipeline words wait in Details; what stays on the row is the one
                  thing a writer acts on — that a change is ready, held, stale or stopped. */}
                {detailed ? null : (
                  <span className={[css.status, STATUS_CLASS[row.status] ?? ''].filter(Boolean).join(' ')}>
                    {STATUS_WORDS[row.status]}
                  </span>
                )}
                {row.words ? (
                  <span className={css.words} data-testid="basil-change-words">
                    <span className={css.before}>{row.words.before}</span>
                    <span className={css.arrow} aria-hidden="true">{ARROW}</span>
                    <span className={css.after}>{row.words.after}</span>
                  </span>
                ) : null}
                {row.expectedCounts || detailed ? (
                  <details className={css.details} data-testid="basil-change-details">
                    <summary className={css.summary}>{DETAILS}</summary>
                    {detailed ? (
                      <span className={css.detail} data-testid="basil-change-status">{statusDetail(row.status)}</span>
                    ) : null}
                    {row.expectedCounts ? (
                      <span className={css.detail}>
                        {`Meant to change ${row.expectedCounts.updatedNodes} scene${row.expectedCounts.updatedNodes === 1 ? '' : 's'}, ${row.expectedCounts.updatedSteps} line${row.expectedCounts.updatedSteps === 1 ? '' : 's'} and ${row.expectedCounts.updatedAnswers} answer${row.expectedCounts.updatedAnswers === 1 ? '' : 's'}.`}
                      </span>
                    ) : null}
                  </details>
                ) : null}
                {row.message ? <span className={css.detail}>{row.message}</span> : null}
                {row.opensScene && row.branchId != null && onOpenScene ? (
                  <button
                    type="button"
                    className={css.openScene}
                    onClick={() => onOpenScene(Number(row.branchId))}
                  >
                    {OPEN_THIS_SCENE}
                  </button>
                ) : null}
                {(warningsByRow?.[row.key] ?? []).map((warning) => (
                  <span className={css.warning} key={`${warning.field}-${warning.message}`}>
                    {`${warning.field}: ${warning.message}`}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      ))}
      {(details ?? []).map((detail) => (
        <p className={css.warning} key={detail.message}>{detail.message}</p>
      ))}
    </section>
  );
}
